import { delay } from 'redux-saga/effects';
import { call, cancel, fork, put, select, take } from 'redux-saga/effects';
import { getDate, getLayout, getPlaybackSpeed, getStreamPosition, isVideoConnected, isLive } from 'selectors/monitoringSelectors';
import { setDate, setStreamPosition } from 'actions/monitoringActions';
import { timelinesRedraw } from 'actions/timelineActions';
import { getTimezoneOffsetCalculator, getRefreshRate } from 'selectors/serverSelectors';
import { CellIndex, UtcTime } from 'types';
import * as moment from 'moment';

export function* loginWatcher() {
    while (true) {
        yield take('LOGIN_SUCCESS');
        const timeKeeper = yield fork(timeKeeperSaga);
        yield take('LOGOUT');
        yield cancel(timeKeeper);
    }
}

export function* timeKeeperSaga() {
    const TICK = 1000;
    const TIMELINE_UPDATE_INTERVAL: number = yield select(getRefreshRate);
    let ticksSinceTimelineUpdate = 0;
    let lastDay = moment.utc(0);
    let forceUpdateTimeline = false;
    while (true) {
        let utcNow = yield call(Date.now);

        // Did the date wrap over to the next day?
        const tzOffsetCalculator = yield select(getTimezoneOffsetCalculator);
        const currentDay = moment.utc(utcNow).utcOffset(tzOffsetCalculator(utcNow)).startOf('day');
        if (!currentDay.isSame(lastDay, 'day')) {
            // Update the selected date if it is set to yesterday
            const utcDate = yield select(getDate);
            const date = moment.utc(utcDate).utcOffset(tzOffsetCalculator(utcDate));
            if (date.isSame(lastDay, 'day')) {
                date.add(1, 'day'); // TODO: might have to add more than one day... maybe set to current day instead
                yield put(setDate({ date: date.utcOffset(0).toDate(), preserveTimeOfDay: false })); // TODO: verify the UTC calculations are correct
            }
            lastDay = currentDay;
            forceUpdateTimeline = true; // force a timeline update when the date rolls over
        }

        // Advance the position indicators and update the timelines
        if (++ticksSinceTimelineUpdate >= TIMELINE_UPDATE_INTERVAL || forceUpdateTimeline) {
            yield call(updateStreamPositionsSaga, TICK * ticksSinceTimelineUpdate, utcNow);
            yield put(timelinesRedraw());
            ticksSinceTimelineUpdate = 0;
            forceUpdateTimeline = false;
        }

        utcNow = yield call(Date.now);
        const millisecondsRemaining = TICK - (utcNow % TICK);
        yield delay(millisecondsRemaining);
    }
}

// Update the position indicator on all the timelines
export function* updateStreamPositionsSaga(elapsed: number, now: UtcTime) {
    const nCells = yield select(getLayout);
    for (let cellIndex = 0 as CellIndex; cellIndex < nCells + 1; cellIndex++) {
        const connected = yield select(isVideoConnected, { cellIndex });
        if (!connected) continue;
        
        const playbackSpeed = yield select(getPlaybackSpeed, { cellIndex });
        if (!playbackSpeed) continue;

        const live = yield select(isLive, { cellIndex });
        if (live) {
            yield put(setStreamPosition(now, { cellIndex }));
            continue;
        }

        // Update the stream position
        let streamPosition = yield select(getStreamPosition, { cellIndex });
        streamPosition = Math.min(streamPosition + elapsed * playbackSpeed, now);
        yield put(setStreamPosition(streamPosition, { cellIndex }));
    }
}



// WEBPACK FOOTER //
// ./src/sagas/monitoring/timeKeeperSaga.ts